import {
  UseQueryOptions,
  useQueries,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { AllowedLocales } from 'utils/date'

import {
  validateFlatActivityValue,
  ValidateFlatActivityValueProps
} from 'utils/validateActivityValue'

type ValidableActivity = Pick<
  ValidateFlatActivityValueProps,
  'activityModel' | 'value'
>

const getValidateActivityQueryOptions = ({
  activityModel,
  value,
  context
}: ValidateFlatActivityValueProps) =>
  ({
    queryKey: ['validateFlatActivityValue', activityModel.uuid, value, context],
    queryFn: () => validateFlatActivityValue({ activityModel, value, context }),
    initialData: {
      validationStatus: 'pending' as ValidationStatus,
      helperText: ''
    }
  }) satisfies UseQueryOptions

const useValidationErrorContext =
  (): ValidateFlatActivityValueProps['context'] => {
    const { language } = useTranslation()

    return useMemo(
      () => ({
        lang: language as AllowedLocales
      }),
      [language]
    )
  }

export const useImperativeValidateActivities = () => {
  const queryClient = useQueryClient()
  const context = useValidationErrorContext()

  const validateActivities = useCallback(
    async (activities: ValidableActivity[]) =>
      Promise.all(
        activities.map((activity) =>
          queryClient.fetchQuery(
            getValidateActivityQueryOptions({ ...activity, context })
          )
        )
      ),
    [context, queryClient]
  )

  return validateActivities
}

type UseValidateActivitiesProps = {
  activities: ValidableActivity[]

  enabled?: boolean
}

export const useValidateActivities = ({
  activities,
  enabled
}: UseValidateActivitiesProps) => {
  const context = useValidationErrorContext()

  const results =
    useQueries({
      queries: activities.map(
        (activity) =>
          ({
            ...getValidateActivityQueryOptions({ ...activity, context }),
            enabled
          }) satisfies UseQueryOptions
      )
    }) || []

  type Result = (typeof results)[number]

  type ResultWithInitialData = Omit<Result, 'data'> & {
    data: NonNullable<Result['data']>
  }

  return results as ResultWithInitialData[]
}

type UseValidateActivityProps = {
  activityModel: FlatActivityModel
  value?: ActivityValue
  enabled?: boolean
  debounceTime?: number
}

export default function useValidateActivity({
  activityModel,
  value,
  enabled,
  debounceTime = 300
}: UseValidateActivityProps) {
  const [activities, setActivities] = useState([{ activityModel, value }])

  const debounceActivityValue = () => {
    const timeout = setTimeout(
      () => setActivities([{ activityModel, value }]),
      debounceTime
    )

    return () => clearTimeout(timeout)
  }

  useEffect(debounceActivityValue, [activityModel, value, debounceTime])

  const [{ data, ...queryInfo }] = useValidateActivities({
    activities,
    enabled
  })

  return {
    ...data,
    ...queryInfo
  }
}
